/**
 * Carries the launch splash across the WebView reload that applies an OTA
 * bundle, so one continuous wait does not look like two screens.
 *
 * Applying a bundle replaces the page. That is a brand new document: module
 * state is gone, React remounts, and SplashScreen would re-roll its quip,
 * replay the logo fade, and drop the progress row it had been showing. The
 * player has been watching that same screen for the whole download, so all
 * three read as the launch screen appearing twice.
 *
 * Two things are carried, and the second one matters more than it looks. The
 * QUIP keeps the words identical. The DETAIL keeps the progress row on screen
 * at 100% until the splash itself gives way to the lobby: without it the row
 * simply vanishes, and because the splash is a centred column, losing a child
 * re-centres everything. Measured on device, the logo jumped 58px at that
 * moment, which by itself reads as the screen changing even when the quip and
 * the animation are perfectly preserved.
 *
 * sessionStorage is the right lifetime, and not merely a convenient one: it
 * survives a same-origin reload but starts empty on a genuine cold launch. So
 * "we just applied an update" continues the splash, while a real relaunch gets
 * the full branded intro it should.
 *
 * Every access is guarded. sessionStorage throws outright in some contexts
 * (blocked site data, some WebView configurations), and a missing value must
 * degrade to an ordinary intro rather than take the boot down with it.
 */

import type { SplashDetail } from 'contexts/SplashScreenContext';

const QUIP_KEY = 'hijack:splash-quip';
const CONTINUE_KEY = 'hijack:splash-continue';

export type SplashContinuation = { quip: string | null; detail?: SplashDetail };

/**
 * Remember the quip currently on screen, so the page on the other side of a
 * reload can carry on with it instead of picking a new one.
 */
export const rememberSplashQuip = (quip: string): void => {
  try {
    sessionStorage.setItem(QUIP_KEY, quip);
  } catch {
    // Nothing to do: the next page just gets a fresh quip.
  }
};

/**
 * Called immediately before the apply that reloads the WebView, with whatever
 * the splash is currently showing so the next page can keep showing it.
 *
 * Anything else that reloads the page (a crash, a manual refresh) deliberately
 * does NOT set this, and gets the normal intro.
 */
export const markSplashContinuation = (detail?: SplashDetail): void => {
  try {
    sessionStorage.setItem(CONTINUE_KEY, detail ? JSON.stringify(detail) : '1');
  } catch {
    // Nothing to do: the next page just gets a fresh intro.
  }
};

/**
 * Read and clear the continuation. Single-use: cleared on read so only the
 * first splash after the apply continues, not every later one this session.
 */
export const consumeSplashContinuation = (): SplashContinuation | null => {
  try {
    const raw = sessionStorage.getItem(CONTINUE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CONTINUE_KEY);
    const quip = sessionStorage.getItem(QUIP_KEY);
    if (raw === '1') return { quip };
    // A malformed value still has to continue the splash rather than throw
    // the boot: the quip alone is a better outcome than a replayed intro.
    try {
      return { quip, detail: JSON.parse(raw) as SplashDetail };
    } catch {
      return { quip };
    }
  } catch {
    return null;
  }
};

/**
 * Drop a continuation that is no longer going to happen.
 *
 * The boot gate marks the continuation when the apply starts, then releases
 * only if the apply did NOT replace the page. Reaching that release proves the
 * reload never came, so the marker has to go: otherwise the next splash in
 * this same page session would skip its intro for a reload that never
 * happened.
 */
export const clearSplashContinuation = (): void => {
  try {
    sessionStorage.removeItem(CONTINUE_KEY);
  } catch {
    // Nothing to clean up.
  }
};

/** Test-only: leave no continuation behind between cases. */
export const resetSplashContinuityForTests = (): void => {
  try {
    sessionStorage.removeItem(QUIP_KEY);
    sessionStorage.removeItem(CONTINUE_KEY);
  } catch {
    // Nothing to clean up.
  }
};
